/**
 * 国际化（i18n）系统
 * 支持中英文切换
 */

export type Language = 'zh' | 'en';

export interface Translations {
  // 通用
  appName: string;
  appSlogan: string;
  openSource: string;

  // 搜索框
  searchPlaceholder: string;
  searchButton: string;
  hotSearches: string;
  clear: string;

  // 首页
  welcomeTitle: string;
  welcomeDesc: string;

  // 加载状态
  loading: string;
  searching: string;

  // 结果页
  resultsCount: string;
  searchTime: string;
  searchIntent: string;
  relatedTools: string;
  backToHome: string;

  // 配额
  dailyQuota: string;
  times: string;
  remaining: string;
  cacheTip: string;

  // Toast 通知
  cacheHit: string;
  searchComplete: string;
  rateLimitError: string;
  searchFailed: string;

  // 速率限制
  cooldownError: string;
  dailyLimitError: string;
  rateLimitTitle: string;
  rateLimitStats: string;
  quotaUsed: string;
  quotaRemaining: string;
  cacheUnlimited: string;

  // 收藏
  favorites: string;
  myFavorites: string;
  noFavorites: string;

  // 页脚
  source: string;
  lastUpdate: string;
  aboutUs: string;
  privacyPolicy: string;
  advertising: string;

  // 广告
  ad: string;

  // 隐私政策
  privacyTitle: string;
  privacyLastUpdated: string;
  privacyIntro: string;
  privacyDataCollection: string;
  privacyDataCollectionDesc: string;
  privacyLocalStorage: string;
  privacyLocalStorageDesc: string;
  privacySearchHistory: string;
  privacyFavorites: string;
  privacyLanguage: string;
  privacyRateLimit: string;
  privacySparkData: string;
  privacySparkDataDesc: string;
  privacyApiUsage: string;
  privacyApiUsageDesc: string;
  privacyDataStorage: string;
  privacyDataStorageDesc: string;
  privacyDataSharing: string;
  privacyDataSharingDesc: string;
  privacyThirdParty: string;
  privacyThirdPartyDesc: string;
  privacyCookies: string;
  privacyCookiesDesc: string;
  privacyUserRights: string;
  privacyUserRightsDesc: string;
  privacyDataAccess: string;
  privacyDataDeletion: string;
  privacyDataSecurity: string;
  privacyDataSecurityDesc: string;
  privacyPolicyChanges: string;
  privacyPolicyChangesDesc: string;
  privacyContact: string;
  privacyContactEmail: string;
  close: string;

  // 工具详情
  installation: string;
  ubuntu: string;
  centos: string;
  docker: string;
  macos: string;
  download: string;
  mirror: string;
  official: string;
  commonIssues: string;
  commonCommands: string;
  coreAdvantages: string;
  alternatives: string;
  applicableScenarios: string;
  corePositioning: string;
  copy: string;
  copied: string;

  // 错误
  error: string;
  retry: string;

  // Spark Capture
  sparkCapture: string;
  sparkCaptureSubtitle: string;
  quickCapture: string;
  viewBoard: string;
  title: string;
  description: string;
  notes: string;
  project: string;
  required: string;
  optional: string;
  tipShortcut: string;
  capturing: string;
  captureSuccess: string;
  captureFailed: string;
  timeoutError: string;
  networkError: string;

  // Spark Board
  sparkBoard: string;
  manageIdeas: string;
  refresh: string;
  newSpark: string;
  total: string;
  captured: string;
  thinking: string;
  experimenting: string;
  noSparks: string;
  justNow: string;
  minsAgo: string;
  hoursAgo: string;
  daysAgo: string;
  think: string;
  experiment: string;
  back: string;
  done: string;
  deleteSpark: string;
  deleteConfirm: string;
  loading: string;
}

const translations: Record<Language, Translations> = {
  zh: {
    // 通用
    appName: 'ToolSearch',
    appSlogan: '开源驱动',
    openSource: '开源',

    // 搜索框
    searchPlaceholder: '请输入工具名称（如：zabbix、mysql、golang）',
    searchButton: '搜索',
    hotSearches: '热门搜索：',
    clear: '清除',

    // 首页
    welcomeTitle: '程序员工具搜索平台',
    welcomeDesc: '基于 LLM 的智能工具搜索引擎',

    // 加载状态
    loading: '加载中...',
    searching: '搜索中...',

    // 结果页
    resultsCount: '找到',
    searchTime: '个相关工具',
    searchIntent: '搜索意图',
    relatedTools: '相关工具',
    backToHome: '返回首页',

    // 配额
    dailyQuota: '今日API配额：',
    times: '次',
    remaining: '剩余',
    cacheTip: '💡 缓存查询不计配额',

    // Toast 通知
    cacheHit: '⚡ 从缓存加载',
    searchComplete: '✅ 搜索完成',
    rateLimitError: '⚠️',
    searchFailed: '搜索失败，请稍后重试',

    // 速率限制
    cooldownError: '请等待 {cooldown} 秒后重试',
    dailyLimitError: '今日API调用次数已达上限（{limit}次），缓存查询仍可使用',
    rateLimitTitle: '⚠️ 速率限制',
    rateLimitStats: '今日API调用统计：',
    quotaUsed: '• 已使用：',
    quotaRemaining: '• 剩余：',
    cacheUnlimited: '💡 缓存查询不受限制，已缓存工具可无限次使用',

    // 收藏
    favorites: '收藏工具',
    myFavorites: '我的收藏',
    noFavorites: '暂无收藏的工具',

    // 页脚
    source: '数据来源：开源社区 + AI智能生成',
    lastUpdate: '最后更新：2026-03-08',
    aboutUs: '关于Douya',
    privacyPolicy: '隐私政策',
    advertising: '建议',

    // 广告
    ad: '广告',

    // 隐私政策
    privacyTitle: '隐私政策',
    privacyLastUpdated: '最后更新：2026-03-08',
    privacyIntro: 'Douya Search（"我们"、"本网站"）致力于保护用户隐私。本隐私政策说明了我们如何收集、使用和保护您的信息。',
    privacyDataCollection: '1. 数据收集',
    privacyDataCollectionDesc: '我们使用浏览器本地存储（localStorage）保存以下信息：',
    privacyLocalStorage: '浏览器本地存储',
    privacyLocalStorageDesc: '所有数据仅存储在您的浏览器本地，不会上传至我们的服务器，包括：',
    privacySearchHistory: '• 搜索历史：记录您搜索过的工具名称，便于快速重复搜索',
    privacyFavorites: '• 收藏列表：保存您收藏的工具信息',
    privacyLanguage: '• 语言偏好：记住您选择的语言',
    privacyRateLimit: '• 速率限制计数：记录今日API调用次数，用于配额管理',
    privacySparkData: '• 灵感数据：保存您在"灵感火花随手记"中记录的想法和笔记',
    privacySparkDataDesc: '您的灵感内容完全保存在浏览器本地，我们无法访问这些数据。',
    privacyApiUsage: '2. API调用',
    privacyApiUsageDesc: '当您发起搜索查询时：',
    privacyDataStorage: '3. 数据存储位置',
    privacyDataStorageDesc: '除上述localStorage数据外，本网站不收集、存储或传输任何个人身份信息。我们不需要您注册账户，不使用Cookie追踪用户行为。',
    privacyDataSharing: '4. 数据共享',
    privacyDataSharingDesc: '我们不会向第三方出售、交易或转让您的个人信息。我们仅与以下服务提供商合作：',
    privacyThirdParty: '第三方服务',
    privacyThirdPartyDesc: '• Cloudflare Pages：网站托管服务\n• LLM API提供商：处理搜索查询并返回结果\n  这些服务提供商有自己的隐私政策，我们建议您查阅相关条款。',
    privacyCookies: '5. Cookies',
    privacyCookiesDesc: '本网站不使用追踪型Cookie。Cloudflare Pages可能使用必要的Cookie以提供服务（如CDN缓存），但这些不用于个人身份识别。',
    privacyUserRights: '6. 用户权利',
    privacyUserRightsDesc: '您对自己的数据拥有完全控制权：',
    privacyDataAccess: '• 访问权：通过浏览器开发者工具查看localStorage中的所有数据',
    privacyDataDeletion: '• 删除权：清除浏览器缓存或使用浏览器"清除浏览数据"功能即可删除所有本地数据',
    privacyDataSecurity: '7. 数据安全',
    privacyDataSecurityDesc: '虽然我们采取合理措施保护您的数据安全，但请注意：localStorage数据存储在您的浏览器中，任何能访问您设备的人都可以查看这些数据。建议您在公共设备使用后清除本地数据。',
    privacyPolicyChanges: '8. 政策变更',
    privacyPolicyChangesDesc: '我们可能会不时更新本隐私政策。变更后的政策将在本页面发布，并更新修订日期。重大变更将通过网站通知方式告知。',
    privacyContact: '9. 联系我们',
    privacyContactEmail: '如您对本隐私政策有任何疑问，或需要行使您的数据权利，请通过以下方式联系我们：\n邮箱：douyaworld2026@163.com',
    close: '关闭',

    // 工具详情
    installation: '安装',
    ubuntu: 'Ubuntu',
    centos: 'CentOS',
    docker: 'Docker',
    macos: 'macOS',
    download: '下载',
    mirror: '国内镜像',
    official: '官方链接',
    commonIssues: '常见问题',
    commonCommands: '常用命令',
    coreAdvantages: '核心优势',
    alternatives: '替代方案',
    applicableScenarios: '适用场景',
    corePositioning: '定位',
    copy: '复制',
    copied: '已复制',

    // 错误
    error: '出错了',
    retry: '重试',

    // Flash Ideas Capture
    sparkCapture: '灵感捕获',
    sparkCaptureSubtitle: '快速记录你的想法',
    quickCapture: '快速捕获',
    viewBoard: '查看随手记',
    title: '标题',
    description: '描述',
    notes: '备注',
    project: '项目',
    required: '*',
    optional: '（可选）',
    tipShortcut: '提示：Ctrl+Enter 提交',
    capturing: '捕获中...',
    captureSuccess: '✅ 灵感捕获成功！1秒后跳转到随手记...',
    captureFailed: '捕获失败',
    timeoutError: '⏱️ 请求超时：API服务无响应',
    networkError: '网络错误：',

    // Flash Ideas Board
    sparkBoard: '灵感火花随手记',
    manageIdeas: '管理你的想法',
    refresh: '刷新',
    newSpark: '+ 新建灵感',
    total: '总计',
    captured: '已捕获',
    thinking: '思考中',
    experimenting: '实验中',
    noSparks: '暂无灵感',
    justNow: '刚刚',
    minsAgo: '分钟前',
    hoursAgo: '小时前',
    daysAgo: '天前',
    think: '思考',
    experiment: '实验',
    back: '返回',
    done: '完成',
    deleteSpark: '删除',
    deleteConfirm: '确定删除此火花？',
    loading: '加载中...',
  },

  en: {
    // 通用
    appName: 'ToolSearch',
    appSlogan: 'Open Source Driven',
    openSource: 'Open Source',

    // 搜索框
    searchPlaceholder: 'Enter tool name (e.g., zabbix, mysql, golang)',
    searchButton: 'Search',
    hotSearches: 'Hot Searches: ',
    clear: 'Clear',

    // 首页
    welcomeTitle: 'Programmer Tool Search Platform',
    welcomeDesc: 'LLM-Powered Intelligent Tool Search Engine',

    // 加载状态
    loading: 'Loading...',
    searching: 'Searching...',

    // 结果页
    resultsCount: 'Found',
    searchTime: 'related tools',
    searchIntent: 'Search Intent',
    relatedTools: 'Related Tools',
    backToHome: 'Back to Home',

    // 配额
    dailyQuota: 'Daily API Quota: ',
    times: ' times',
    remaining: 'Remaining',
    cacheTip: '💡 Cache queries are free',

    // Toast 通知
    cacheHit: '⚡ Loaded from cache',
    searchComplete: '✅ Search complete',
    rateLimitError: '⚠️',
    searchFailed: 'Search failed, please try again later',

    // 速率限制
    cooldownError: 'Please wait {cooldown} seconds before retrying',
    dailyLimitError: 'Daily API call limit reached ({limit} calls), cache queries still available',
    rateLimitTitle: '⚠️ Rate Limit',
    rateLimitStats: 'Today\'s API Call Statistics:',
    quotaUsed: '• Used: ',
    quotaRemaining: '• Remaining: ',
    cacheUnlimited: '💡 Cache queries are unlimited, cached tools can be queried infinitely',

    // 收藏
    favorites: 'Favorites',
    myFavorites: 'My Favorites',
    noFavorites: 'No favorites yet',

    // 页脚
    source: 'Data Source: Open Source + AI Generated',
    lastUpdate: 'Last Updated: 2026-03-08',
    aboutUs: 'About Douya',
    privacyPolicy: 'Privacy Policy',
    advertising: 'Suggestion',

    // 广告
    ad: 'Advertisement',

    // Privacy Policy
    privacyTitle: 'Privacy Policy',
    privacyLastUpdated: 'Last Updated: 2026-03-08',
    privacyIntro: 'Douya Search ("we", "this website") is committed to protecting user privacy. This Privacy Policy explains how we collect, use, and protect your information.',
    privacyDataCollection: '1. Data Collection',
    privacyDataCollectionDesc: 'We use browser localStorage to store the following information:',
    privacyLocalStorage: 'Browser Local Storage',
    privacyLocalStorageDesc: 'All data is stored locally in your browser and is never uploaded to our servers, including:',
    privacySearchHistory: '• Search History: Records tool names you\'ve searched for quick repeat searches',
    privacyFavorites: '• Favorites List: Saves your favorited tool information',
    privacyLanguage: '• Language Preference: Remembers your chosen language',
    privacyRateLimit: '• Rate Limit Counter: Tracks daily API call count for quota management',
    privacySparkData: '• Flash Ideas: Saves your ideas and notes from Flash Ideas',
    privacySparkDataDesc: 'Your ideas are stored entirely in your browser locally. We cannot access this data.',
    privacyApiUsage: '2. API Usage',
    privacyApiUsageDesc: 'When you initiate a search query:',
    privacyDataStorage: '3. Data Storage Location',
    privacyDataStorageDesc: 'Beyond the localStorage data mentioned above, this website does not collect, store, or transmit any personally identifiable information. We do not require account registration and do not use cookies to track user behavior.',
    privacyDataSharing: '4. Data Sharing',
    privacyDataSharingDesc: 'We do not sell, trade, or transfer your personal information to third parties. We only work with the following service providers:',
    privacyThirdParty: 'Third-Party Services',
    privacyThirdPartyDesc: '• Cloudflare Pages: Website hosting service\n• LLM API Providers: Process search queries and return results\n  These service providers have their own privacy policies, which we recommend reviewing.',
    privacyCookies: '5. Cookies',
    privacyCookiesDesc: 'This website does not use tracking cookies. Cloudflare Pages may use necessary cookies to provide services (such as CDN caching), but these are not used for personal identification.',
    privacyUserRights: '6. User Rights',
    privacyUserRightsDesc: 'You have full control over your data:',
    privacyDataAccess: '• Access Right: View all data in localStorage through browser developer tools',
    privacyDataDeletion: '• Deletion Right: Clear your browser cache or use "Clear browsing data" to delete all local data',
    privacyDataSecurity: '7. Data Security',
    privacyDataSecurityDesc: 'While we take reasonable measures to protect your data security, please note: localStorage data is stored in your browser and can be viewed by anyone with access to your device. We recommend clearing local data after use on public devices.',
    privacyPolicyChanges: '8. Policy Changes',
    privacyPolicyChangesDesc: 'We may update this Privacy Policy from time to time. Changes will be posted on this page with an updated revision date. Significant changes will be communicated through website notifications.',
    privacyContact: '9. Contact Us',
    privacyContactEmail: 'If you have any questions about this Privacy Policy or need to exercise your data rights, please contact us:\nEmail: douyaworld2026@163.com',
    close: 'Close',

    // 工具详情
    installation: 'Installation',
    ubuntu: 'Ubuntu',
    centos: 'CentOS',
    docker: 'Docker',
    macos: 'macOS',
    download: 'Download',
    mirror: 'China Mirror',
    official: 'Official Link',
    commonIssues: 'Common Issues',
    commonCommands: 'Common Commands',
    coreAdvantages: 'Core Advantages',
    alternatives: 'Alternatives',
    applicableScenarios: 'Applicable Scenarios',
    corePositioning: 'Positioning',
    copy: 'Copy',
    copied: 'Copied',

    // 错误
    error: 'Error',
    retry: 'Retry',

    // Flash Ideas Capture
    sparkCapture: 'Flash Capture',
    sparkCaptureSubtitle: 'Quick capture your ideas',
    quickCapture: 'Quick Capture',
    viewBoard: 'View Notes',
    title: 'Title',
    description: 'Description',
    notes: 'Notes',
    project: 'Project',
    required: '*',
    optional: '(optional)',
    tipShortcut: 'Tip: Ctrl+Enter to submit',
    capturing: 'Capturing...',
    captureSuccess: '✅ Idea captured! Redirecting to notes in 1s...',
    captureFailed: 'Capture failed',
    timeoutError: '⏱️ Request timeout: API service not responding',
    networkError: 'Network error: ',

    // Flash Ideas Board
    sparkBoard: 'Flash Ideas Notes',
    manageIdeas: 'Manage your ideas',
    refresh: 'Refresh',
    newSpark: '+ New Idea',
    total: 'Total',
    captured: 'Captured',
    thinking: 'Thinking',
    experimenting: 'Experimenting',
    noSparks: 'No ideas yet',
    justNow: 'Just now',
    minsAgo: 'm ago',
    hoursAgo: 'h ago',
    daysAgo: 'd ago',
    think: 'Think',
    experiment: 'Experiment',
    back: 'Back',
    done: 'Done',
    deleteSpark: 'Delete',
    deleteConfirm: 'Delete this spark?',
    loading: 'Loading...',
  },
};

/**
 * 获取翻译文本
 */
export function t(key: keyof Translations, lang: Language = 'zh'): string {
  return translations[lang][key];
}

/**
 * 格式化翻译文本（支持变量替换）
 */
export function tf(
  key: keyof Translations,
  params: Record<string, string | number>,
  lang: Language = 'zh'
): string {
  let text = translations[lang][key];
  Object.keys(params).forEach(paramKey => {
    text = text.replace(`{${paramKey}}`, String(params[paramKey]));
  });
  return text;
}

/**
 * 获取所有翻译
 */
export function getTranslations(lang: Language): Translations {
  return translations[lang];
}
